import React from "react";
import {
    Controller,
    ControllerRenderProps,
    FieldValues,
    useFormContext,
} from "react-hook-form";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { ICheckboxGroup } from "./ExlyCheckboxGroup.interfaces";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

const ExlyCheckboxGroup = (props: ICheckboxGroup) => {
    const {
        name,
        labelProps,
        checkboxProps,
        classes,
        options = [],
        CustomErrorMessageComponent,
        registerOptions,
        onChange,
    } = props;
    const {
        control,
        getValues,
        clearErrors,
        formState: { errors },
    } = useFormContext();
    const data = getValues();
    // considering "" (empty string) is not added as an option
    const error = name ? errors[name] : null;
    const showError = !!error;
    const { message: errorMessage } = error || {};

    /**
     * If the user has some values selected for the 'name' of the field,
     * then we show them as pre selected.
     * This will happen in case of multiple users, edit flow where we have to show
     * the data as prefilled to the user
     */
    const preFilledValues: { [key: string]: string } = {};
    let preFilledValuesArray: string[] = [];

    if (name && data?.[name]) {
        if (Array.isArray(data?.[name])) {
            preFilledValuesArray = [...data?.[name]];
        }
    }

    preFilledValuesArray?.forEach(value => {
        preFilledValues[value] = value;
    });

    /**
     * selectedCheckbox is an object type with key value pair as option string
     * of the the ones selected by the user. Reason being, it is less complex
     * to perform addition and deletion in object types
     */
    const [selectedCheckbox, setSelectedCheckbox] =
        React.useState<Record<string, string>>(preFilledValues);

    /**
     * This function checks whether the option value already checked by the user,
     - if so, then remove that option value and send the updated selected values
     * array to the parent onChange callback
     - else, then set the option in the selected array and send it to the parent
     * onChange callback
     * @param { string } option Option is string type of the option checked or unchecked by the user
     */
    const handleChange = (
        option: string,
        field: ControllerRenderProps<FieldValues, string>
    ) => {
        if (selectedCheckbox[option]) {
            const newSelectedOption = { ...selectedCheckbox };
            delete newSelectedOption[option];
            setSelectedCheckbox({ ...newSelectedOption });
            onChange(Object.keys(newSelectedOption));
            field.onChange(Object.keys(newSelectedOption));
        } else {
            const newSelectedOption = {
                ...selectedCheckbox,
                [option]: option,
            };
            setSelectedCheckbox({
                ...newSelectedOption,
            });
            onChange(Object.keys(newSelectedOption));
            field.onChange(Object.keys(newSelectedOption));
            // if we have an active error state, then on checked click
            // of a check box, we clear that error
            if (showError) {
                clearErrors(name);
            }
        }
    };

    return (
        <div>
            <Controller
                name={`${name}`}
                control={control}
                defaultValue={preFilledValuesArray}
                render={({ field }) => (
                    <FormGroup className={classes?.optionsStyle}>
                        {options.map((option, idx) => (
                            <FormControlLabel
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${option}-${idx}`}
                                control={
                                    <Checkbox
                                        {...checkboxProps}
                                        checked={Boolean(
                                            selectedCheckbox[option]
                                        )}
                                        onChange={() => {
                                            handleChange(option, field);
                                        }}
                                    />
                                }
                                label={option}
                                {...labelProps}
                            />
                        ))}
                    </FormGroup>
                )}
                rules={registerOptions}
            />
            <ErrorMessage
                showError={showError}
                classes={classes}
                CustomErrorMessageComponent={CustomErrorMessageComponent}
                errorMessage={errorMessage}
            />
        </div>
    );
};

export default ExlyCheckboxGroup;
